// frontend/src/infrastructure/websocket/AiWebSocketClient.ts
//
// Prosty klient WebSocket domykający pętlę "Woki-Toki" po stronie frontu:
//   Frontend -> Node (POST /api/sync, ai_query) -> Python -> Node -> Frontend (WS).
//
// Zadanie tej klasy jest wąskie: utrzymać JEDNO stabilne połączenie do backendu
// Node.js (kanał `/ws/commands`, ten sam, na którym backend rozgłasza Read
// Receipts) i dać warstwie prezentacji mechanizm rejestrowania callbacków na
// komunikat `{ type: 'AI_RESPONSE', messageId, answer }`.
//
// Warstwa infrastruktury: żaden inny moduł nie tworzy `WebSocket` do tego kanału.

/** Typ komunikatu z gotową odpowiedzią AI — zgodny z backendem (WSServer.broadcastAiResponse). */
export const AI_RESPONSE_EVENT = 'AI_RESPONSE' as const

/** Ścieżka kanału komend głosowych po stronie backendu Node.js (WSServer.DEFAULT_WS_PATH). */
export const AI_WS_PATH = '/ws/commands'

/** Handler wołany po otrzymaniu odpowiedzi AI: `(messageId, answer)`. */
export type AiResponseHandler = (messageId: string, answer: string) => void

/**
 * Minimalny kontrakt gniazda, jakiego potrzebuje klient. Prawdziwy `WebSocket`
 * przeglądarki spełnia go strukturalnie; w testach podstawiamy atrapę.
 */
export interface WebSocketLike {
  readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((event: unknown) => void) | null
  onclose: ((event: unknown) => void) | null
  onerror: ((event: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
}

/** Fabryka gniazda — wstrzykiwalna na potrzeby testów i nietypowych środowisk. */
export type WebSocketFactory = (url: string) => WebSocketLike

export interface AiWebSocketClientOptions {
  /** Pełny adres kanału. Domyślnie wyliczany z `location` + {@link AI_WS_PATH}. */
  url?: string
  /** Fabryka gniazda. Domyślnie `new WebSocket(url)`. */
  socketFactory?: WebSocketFactory
  /** Czy po zerwaniu połączenia próbować wznowić. Domyślnie `true`. */
  autoReconnect?: boolean
  /** Odstęp przed próbą wznowienia połączenia (ms). Domyślnie `3000`. */
  reconnectDelayMs?: number
}

/** `WebSocket.OPEN` bez zależności od globala (spójne z RFC: 0/1/2/3). */
const WS_OPEN = 1

/** Adres kanału AI wyliczony z bieżącej lokalizacji (ten sam host co API). */
function defaultUrl(): string {
  const hasLocation = typeof location !== 'undefined'
  const secure = hasLocation && location.protocol === 'https:'
  const host = hasLocation ? location.host : 'localhost'
  return `${secure ? 'wss' : 'ws'}://${host}${AI_WS_PATH}`
}

const defaultSocketFactory: WebSocketFactory = (url) =>
  new WebSocket(url) as unknown as WebSocketLike

/** Kształt komunikatu z odpowiedzią AI po deserializacji JSON. */
interface AiResponseMessage {
  type: typeof AI_RESPONSE_EVENT
  messageId: string
  answer: string
}

function isAiResponseMessage(value: unknown): value is AiResponseMessage {
  if (typeof value !== 'object' || value === null) return false
  const msg = value as Record<string, unknown>
  return (
    msg.type === AI_RESPONSE_EVENT &&
    typeof msg.messageId === 'string' &&
    typeof msg.answer === 'string'
  )
}

/**
 * Utrzymuje jedno połączenie WebSocket do kanału AI backendu i rozdziela
 * przychodzące odpowiedzi (`AI_RESPONSE`) do zarejestrowanych handlerów.
 */
export class AiWebSocketClient {
  private readonly url: string
  private readonly socketFactory: WebSocketFactory
  private readonly autoReconnect: boolean
  private readonly reconnectDelayMs: number

  private socket: WebSocketLike | null = null
  private readonly handlers = new Set<AiResponseHandler>()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** Ustawiane przez {@link close} — blokuje auto-reconnect do jawnego {@link connect}. */
  private closedByUser = false

  constructor(options: AiWebSocketClientOptions = {}) {
    this.url = options.url ?? defaultUrl()
    this.socketFactory = options.socketFactory ?? defaultSocketFactory
    this.autoReconnect = options.autoReconnect ?? true
    this.reconnectDelayMs = options.reconnectDelayMs ?? 3000
  }

  /** Otwiera połączenie, jeśli jeszcze nie istnieje. Idempotentne. */
  connect(): void {
    this.closedByUser = false
    this.clearReconnectTimer()
    if (this.socket) return

    const socket = this.socketFactory(this.url)
    this.socket = socket

    socket.onmessage = (event) => this.handleMessage(event.data)
    socket.onclose = (event) => this.handleClose(event)
    socket.onerror = () => {
      // Sam błąd gniazda nie jest fatalny — zaraz po nim przychodzi `onclose`,
      // które (jeśli trzeba) zaplanuje ponowne połączenie. Tu tylko diagnostyka.
      console.error('[AiWebSocketClient] Błąd połączenia z kanałem AI.')
    }
    socket.onopen = () => {
      console.info('[AiWebSocketClient] Połączono z kanałem AI (%s).', this.url)
    }
  }

  /**
   * Rejestruje handler wołany dla KAŻDEJ odpowiedzi AI (`AI_RESPONSE`).
   * Pierwsza rejestracja leniwie otwiera połączenie. Zwraca funkcję
   * wyrejestrowującą — wywołujący filtruje po `messageId` i sam się odpina
   * (nasłuch jednorazowy).
   */
  onAiResponse(handler: AiResponseHandler): () => void {
    this.handlers.add(handler)
    this.connect()
    return () => {
      this.handlers.delete(handler)
    }
  }

  /** Zamyka połączenie na życzenie i wyłącza auto-reconnect do czasu `connect()`. */
  close(): void {
    this.closedByUser = true
    this.clearReconnectTimer()
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
  }

  /** Czy połączenie jest w stanie OPEN. */
  get connected(): boolean {
    return this.socket?.readyState === WS_OPEN
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return

    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      return
    }
    if (!isAiResponseMessage(parsed)) return

    // Kopia zbioru — handler może się wyrejestrować w trakcie iteracji.
    for (const handler of [...this.handlers]) {
      try {
        handler(parsed.messageId, parsed.answer)
      } catch (error) {
        console.error('[AiWebSocketClient] Handler odpowiedzi AI rzucił wyjątek.', error)
      }
    }
  }

  /**
   * `CloseEvent` przeglądarki nie trafia do `console.error` przez `onerror`
   * (ten jest zawsze pusty ze względów bezpieczeństwa) — dopiero tu, w
   * `code`/`reason`, jest realny powód zerwania (np. 1006 = zerwane bez
   * handshake'u, typowe dla niestabilnego łącza/NAT). Logujemy to jawnie,
   * bo bez tego diagnoza zrywanych połączeń WS sprowadza się do zgadywania.
   */
  private handleClose(event?: unknown): void {
    const e = event as { code?: number; reason?: string } | undefined
    console.error(
      `[AiWebSocketClient] Kanał AI rozłączony (code=${e?.code}, reason="${e?.reason ?? ''}").`,
    )
    this.socket = null
    if (this.closedByUser || !this.autoReconnect) return
    if (this.reconnectTimer !== null) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, this.reconnectDelayMs)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}

let singleton: AiWebSocketClient | null = null

/** Współdzielona instancja klienta — jedno stabilne połączenie na całą aplikację. */
export function getAiWebSocketClient(): AiWebSocketClient {
  if (singleton === null) {
    singleton = new AiWebSocketClient()
  }
  return singleton
}
